import { useEffect, useMemo, useState } from "react";
import { adminFetch } from "./adminApi";

function Chip({ children }) {
  return (
    <span className="inline-flex items-center rounded-full bg-white/10 px-2 py-0.5 text-xs text-white/80">
      {children}
    </span>
  );
}

function Modal({ open, title, children, onClose }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="absolute left-1/2 top-1/2 w-[min(520px,92vw)] -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-[#272c39] p-4 ring-1 ring-white/10 shadow-xl">
        <div className="flex items-center justify-between">
          <div className="font-unbounded text-lg font-bold text-white">{title}</div>
          <button
            onClick={onClose}
            className="rounded-lg bg-white/10 px-3 py-1.5 text-sm text-white/80 hover:bg-white/15"
          >
            Close
          </button>
        </div>
        <div className="mt-3">{children}</div>
      </div>
    </div>
  );
}

export default function AdminWallets() {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const pageSize = 20;

  // modal state
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState(null); // user object with wallet
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const query = useMemo(() => {
    const p = new URLSearchParams();
    p.set("page", String(page));
    p.set("pageSize", String(pageSize));
    if (q.trim()) p.set("q", q.trim());
    return p.toString();
  }, [page, q]);

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const data = await adminFetch(`/api/admin/wallets?${query}`);
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotal(Number(data?.total || 0));
    } catch (e) {
      setErr(e?.message || "Failed to load wallets");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const canPrev = page > 1;
  const canNext = page * pageSize < total;

  function openAdjust(user) {
    setTarget(user);
    setAmount("");
    setReason("");
    setOpen(true);
  }

  function closeAdjust() {
    setOpen(false);
    setTarget(null);
    setAmount("");
    setReason("");
    setBusy(false);
  }

  async function applyAdjust() {
    if (!target?.id) return;

    const n = Number(String(amount).replace(",", "."));
    if (!Number.isFinite(n) || n === 0) return alert("Amount must be a non-zero number (e.g. 50 or -50)");
    const r = String(reason || "").trim();
    if (!r) return alert("Reason is required");

    setBusy(true);
    try {
      await adminFetch(`/api/admin/wallets/${target.id}/adjust`, {
        method: "POST",
        body: { amount: n, reason: r },
      });

      // reload list + keep modal open with updated balance
      await load();

      // find updated user in current page (best effort)
      const refreshed = items.find((x) => x.id === target.id);
      if (refreshed) setTarget(refreshed);

      setAmount("");
      setReason("");
      alert("Wallet updated ✅");
    } catch (e) {
      alert(e?.data?.error || e?.message || "Failed to adjust wallet");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="text-white">
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="font-unbounded text-2xl font-bold">Wallets</h2>
          <p className="mt-2 text-white/70">Search by tutorId/email/name + manual balance adjust.</p>
        </div>

        <div className="flex flex-col gap-2 md:flex-row md:items-center">
          <input
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            placeholder="Search tutorId/email/name…"
            className="w-full md:w-72 rounded-lg bg-white/10 px-3 py-2 text-sm outline-none ring-1 ring-white/10 focus:ring-white/30"
          />
          <button
            onClick={load}
            className="rounded-lg bg-white/10 px-3 py-2 text-sm hover:bg-white/15 ring-1 ring-white/10"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="mt-5 rounded-2xl bg-white/5 ring-1 ring-white/10 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div className="text-sm text-white/70">{loading ? "Loading…" : `Total: ${total}`}</div>
          <div className="flex items-center gap-2">
            <button
              disabled={!canPrev}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-lg bg-white/10 px-3 py-1.5 text-sm disabled:opacity-40 hover:bg-white/15"
            >
              Prev
            </button>
            <Chip>Page {page}</Chip>
            <button
              disabled={!canNext}
              onClick={() => setPage((p) => p + 1)}
              className="rounded-lg bg-white/10 px-3 py-1.5 text-sm disabled:opacity-40 hover:bg-white/15"
            >
              Next
            </button>
          </div>
        </div>

        {err && <div className="px-4 py-3 text-sm text-red-300">{err}</div>}

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-white/60">
              <tr className="border-b border-white/10">
                <th className="px-4 py-3 text-left">Tutor</th>
                <th className="px-4 py-3 text-left">Balance</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loading && items.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-4 py-6 text-white/60">
                    No tutors found
                  </td>
                </tr>
              ) : (
                items.map((u) => (
                  <tr key={u.id} className="border-b border-white/10">
                    <td className="px-4 py-3">
                      <div className="font-medium text-white/90">{u.name || "—"}</div>
                      <div className="text-white/60">{u.email}</div>
                      <div className="text-white/40 text-xs break-all">{u.id}</div>
                    </td>
                    <td className="px-4 py-3">
                      <Chip>balance: {u.wallet?.balance ?? 0}</Chip>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => openAdjust(u)}
                        className="rounded-lg bg-white/10 px-3 py-1.5 text-xs hover:bg-white/15 ring-1 ring-white/10"
                      >
                        Adjust
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        open={open}
        title="Adjust wallet"
        onClose={() => {
          if (!busy) closeAdjust();
        }}
      >
        {!target ? (
          <div className="text-sm text-white/70">No tutor selected.</div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-xl bg-white/5 ring-1 ring-white/10 p-3">
              <div className="text-sm text-white/90 font-semibold">{target.name || "Tutor"}</div>
              <div className="text-xs text-white/60">{target.email}</div>
              <div className="mt-2">
                <Chip>current: {target.wallet?.balance ?? 0}</Chip>
              </div>
            </div>

            <div className="grid gap-2">
              <div>
                <div className="text-xs text-white/50">Amount (coins)</div>
                <input
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="e.g. 100 or -100"
                  className="w-full rounded-lg bg-white/10 px-3 py-2 text-sm outline-none ring-1 ring-white/10 focus:ring-white/30"
                />
                <div className="mt-1 text-xs text-white/50">
                  Positive = add coins, negative = subtract.
                </div>
              </div>

              <div>
                <div className="text-xs text-white/50">Reason (required)</div>
                <input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. Refund for booking #..., bug compensation…"
                  className="w-full rounded-lg bg-white/10 px-3 py-2 text-sm outline-none ring-1 ring-white/10 focus:ring-white/30"
                />
              </div>

              <button
                disabled={busy}
                onClick={applyAdjust}
                className="rounded-lg bg-white/10 px-3 py-2 text-sm hover:bg-white/15 ring-1 ring-white/10 disabled:opacity-50"
              >
                {busy ? "Applying…" : "Apply"}
              </button>

              <div className="text-xs text-white/50">
                This creates TutorTransaction type <b>ADMIN_ADJUST</b> + AdminAuditLog action <b>WALLET_ADJUST</b>.
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
